import clsx from "clsx";
import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { FeedProductCard, getProductIdFromUrl } from "web/react/components/feed-grid/feed-grid";
import { FeedNoResults } from "web/react/components/feed-no-results/feed-no-results";
import { FeedsShowMore } from "web/react/components/feeds-show-more/feeds-show-more";
import { KeylineGrid, KeylineGridItem } from "web/react/components/keyline-grid";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useInitialReduxState } from "web/react/hooks/use-inital-redux-state/use-initial-redux-state";
import { useProductFeed } from "web/react/hooks/use-product-feed/use-product-feed";
import { useShopfrontSectionImpression } from "web/react/pages/feed/shopfront-feed/use-shopfront-section-impression";
import { FeedPage } from "web/react/pages/feed/utils";
import { setFeedData } from "web/redux/ducks/feed";
import { setAll as setShowMoreAll } from "web/redux/ducks/feed-show-more";
import { ReduxStoreState } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import { getInitialFeedProductIds } from "web/script/utils/factories";
import { ProductFeedSerializer } from "web/types/serializers";
import * as styles from "./shopfront-feed.css";

const FIRST_GRID_SIZE = 8;
// The tablet grid is 3 columns, so 8 products leaves 2 orphans;
// a 9th product completes the third row there
const TABLET_FIRST_GRID_SIZE = 9;

// CW-3710: click reason for product cards, per grid — the top/bottom split only
// happens client-side (viewport-dependent), so the backend can't assign these;
// see product_feed.py's shared impression reason for the same constraint.
const TOP_GRID_CLICK_REASON = "shopfront-feeds-1-product";
const BOTTOM_GRID_CLICK_REASON = "shopfront-feeds-2-product";

export interface ShopfrontFeedGridProps {
    data: ProductFeedSerializer;
    feedType: FeedPage | string;
    // The rail and media slot sections rendered between the two grids
    children?: React.ReactNode;
}

interface GridSliceProps {
    feedItems: ProductFeedSerializer["feed_items"];
    feedType: FeedPage | string;
    isFetching: boolean;
    positionOffset: number;
    clickReason: string;
}

function GridSlice({
    feedItems,
    feedType,
    isFetching,
    positionOffset,
    clickReason,
}: GridSliceProps): React.ReactElement {
    const productId = getProductIdFromUrl();

    // Re-tagged once per slice rather than per render: the card object is passed
    // straight into ProductCard's context, so a fresh object each render would
    // invalidate every consumer downstream.
    const products = useMemo(
        () =>
            feedItems.map(({ product_card: product }) => ({
                ...product,
                context_type: clickReason,
            })),
        [feedItems, clickReason]
    );

    return (
        <KeylineGrid className={clsx(isFetching && styles.loading)}>
            {products.map((product, i) => (
                <KeylineGridItem
                    key={`${product.uid} ${product.link_id}`}
                    scrollIntoView={productId === product.id}
                >
                    <FeedProductCard
                        product={product}
                        feedType={feedType}
                        productPosition={positionOffset + i + 1}
                    />
                </KeylineGridItem>
            ))}
        </KeylineGrid>
    );
}

export function ShopfrontFeedGrid({
    data,
    feedType,
    children,
}: ShopfrontFeedGridProps): React.ReactElement {
    useInitialReduxState([
        [setFeedData, data],
        [setShowMoreAll, data.show_more_button],
    ]);

    const feedData = useSelector((state: ReduxStoreState) => state.feed.data);
    const isFetching = useSelector((state: ReduxStoreState) => state.feed.ui.isFetching);
    const { isTabletViewport } = useDomViewport();
    useProductFeed();

    // Fall back to the SSR props until redux is seeded on the client
    const feedGridData = feedData.feed_items.length ? feedData : data;
    const feedItems = feedGridData.feed_items;

    const productIds = getInitialFeedProductIds(feedItems);

    useEffect(() => {
        analytics.event({
            category: "feed",
            action: "view",
            label: "feed_product_ids",
            nonInteraction: true,
            customData: { ids: productIds },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(productIds)]);

    // This grid holds every remaining product, so it is far taller than a phone
    // viewport and 30% of it is never on screen at once. Fire as soon as any of it is.
    const bottomFeedRef = useShopfrontSectionImpression<HTMLDivElement>("retailer_bottom_feed", 0);

    if (!feedItems.length) {
        return <FeedNoResults data={feedGridData} feedType={feedType as FeedPage} />;
    }

    const firstGridSize = isTabletViewport ? TABLET_FIRST_GRID_SIZE : FIRST_GRID_SIZE;
    const firstItems = feedItems.slice(0, firstGridSize);
    const restItems = feedItems.slice(firstGridSize);

    return (
        <div data-testid="shopfront-feed-grid" className={styles.sections}>
            <div data-testid="shopfront-first-grid" className={styles.gutter}>
                <div className={styles.contentWidth}>
                    <GridSlice
                        feedItems={firstItems}
                        feedType={feedType}
                        isFetching={isFetching}
                        positionOffset={0}
                        clickReason={TOP_GRID_CLICK_REASON}
                    />
                </div>
            </div>

            {children}

            {restItems.length > 0 && (
                <div
                    ref={bottomFeedRef}
                    data-testid="shopfront-rest-grid"
                    className={styles.gutter}
                >
                    <div className={styles.contentWidth}>
                        <GridSlice
                            feedItems={restItems}
                            feedType={feedType}
                            isFetching={isFetching}
                            positionOffset={firstGridSize}
                            clickReason={BOTTOM_GRID_CLICK_REASON}
                        />
                    </div>
                </div>
            )}

            <div className={styles.gutter}>
                <div className={styles.contentWidth}>
                    <FeedsShowMore />
                </div>
            </div>
        </div>
    );
}
